import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { Bell, Check, Info, ShoppingCart, Ticket, User, AlertTriangle } from 'lucide-react';

const icons = {
  info: Info,
  success: Check,
  warning: AlertTriangle,
  error: AlertTriangle,
  order: ShoppingCart,
  ticket: Ticket,
  user: User,
  system: Bell
};

const colors = {
  info: 'bg-blue-100 text-blue-600',
  success: 'bg-green-100 text-green-600',
  warning: 'bg-amber-100 text-amber-600',
  error: 'bg-red-100 text-red-600',
  order: 'bg-emerald-100 text-emerald-600',
  ticket: 'bg-purple-100 text-purple-600',
  user: 'bg-indigo-100 text-indigo-600',
  system: 'bg-slate-100 text-slate-600'
};

const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  // 1. Fetch Notifications (Real-time polling)
  const { data: rawData } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => axiosClient.get('/admin/notifications?limit=10'),
    refetchInterval: 10000,
  });

  const notifications = Array.isArray(rawData) ? rawData : [];

  const unreadCount = notifications.filter(n => !n.is_read).length;

  // 2. Mark as Read Mutation
  const markReadMutation = useMutation({
    mutationFn: (id) => axiosClient.post(`/admin/notifications/${id}/read`),
    onSuccess: () => queryClient.invalidateQueries(['notifications'])
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => axiosClient.post(`/admin/notifications/read-all`),
    onSuccess: () => queryClient.invalidateQueries(['notifications'])
  });

  const handleMarkRead = (id) => {
    markReadMutation.mutate(id);
  };

  return (
    <div className="relative">
      {/* Bell Button */}
      <button 
        className="p-2 rounded-full hover:bg-slate-100 relative text-slate-600 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border border-white animate-pulse"></span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)}></div>
          <div className="absolute right-0 mt-3 w-80 bg-white rounded-xl shadow-xl border border-slate-100 z-20 overflow-hidden animate-in fade-in zoom-in-95 duration-200 origin-top-right">
            <div className="p-4 border-b border-slate-50 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-semibold text-slate-800 text-sm">Notifications</h3>
              {unreadCount > 0 && (
                <button 
                  onClick={() => markAllReadMutation.mutate()}
                  className="text-xs text-airs-600 hover:text-airs-700 font-medium"
                >
                  Mark all read
                </button>
              )}
            </div>
            
            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-slate-400 text-sm">
                  No new notifications
                </div>
              ) : (
                notifications.map((notif) => {
                  const Icon = icons[notif.type] || Bell;
                  const colorClass = colors[notif.type] || colors.system;
                  
                  return (
                    <div 
                      key={notif.id} 
                      className={`p-4 border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer ${notif.is_read ? 'opacity-60' : 'bg-blue-50/30'}`}
                      onClick={() => !notif.is_read && handleMarkRead(notif.id)}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`p-2 rounded-lg ${colorClass} flex-shrink-0 mt-0.5`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${notif.is_read ? 'text-slate-600' : 'text-slate-800 font-medium'} truncate`}>
                            {notif.title}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                            {notif.message}
                          </p>
                          <p className="text-[10px] text-slate-400 mt-2">
                            {new Date(notif.created_at).toLocaleString()}
                          </p>
                        </div>
                        {!notif.is_read && (
                           <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2"></div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationDropdown;